// ---------------------------------------------------------------------------
// The navigation map.
//
// This single list drives three things at once, so they can never disagree:
//   1. which links appear in the sidebar,
//   2. which routes the router will let someone open,
//   3. where a person is sent after signing in.
//
// A cashier holds pos.access but not dashboard.view, so signing in as a
// cashier produces exactly: Point of Sale, Orders, Customers, My Shift.
// Everything else disappears from the menu AND is refused by the route guard
// AND is refused by the API. Hiding a link is a convenience, not the defence.
//
// `phase` marks a screen that is not built yet. Those routes render an honest
// "not built yet" page listing what will be there. They are never wired to
// buttons that appear to work.
// ---------------------------------------------------------------------------

import type { Translations } from '../i18n/translations';

export type NavGroupKey = 'selling' | 'pricing' | 'menu' | 'stock' | 'buying' | 'money' | 'admin';

export interface NavItem {
  /// URL path, e.g. '/pos'
  path: string;
  /// Which label to read out of the dictionary.
  labelKey: keyof Translations['nav'];
  /// Lucide icon name, resolved in the Sidebar.
  icon: string;
  /// The permission required. Holding it shows the link and opens the route.
  permission: string;
  group: NavGroupKey;
  /// Absent means the screen is built and working in Phase 1.
  /// Present means it is planned; the route shows a clearly-marked notice.
  phase?: keyof Translations['phases'];
  /// Short description shown on the "not built yet" page.
  plannedContent?: string[];
  /// The module is BUILT but deliberately paused: its tables, models and
  /// permissions are intact and its data is safe, but the operational screen
  /// is withdrawn until it is finished. The sidebar says so, and the route
  /// shows a Coming Soon page.
  comingSoon?: boolean;

  /// The sensitive things somebody can do INSIDE this module, as registered
  /// permission keys.
  ///
  /// Kept here, beside the route and the module permission, so the sidebar,
  /// the route guards and the permission editor all read one list. A second
  /// list in the editor would drift the first time a screen gained a button.
  ///
  /// `permission` above is the key that opens the module; these are the keys
  /// that decide what is usable once it is open. A module with no dangerous
  /// action - the dashboard, the audit log - has none.
  actions?: string[];

  /// Read-only permissions this module cannot work without, which the SERVER
  /// grants automatically to anybody holding `permission`.
  ///
  /// Mirrors `IMPLIED_PERMISSIONS` in the backend's permission service, which
  /// is the authority - this copy exists so the editor can show the reader
  /// why a box is ticked and locked. The two are kept honest by a test that
  /// compares them.
  implies?: string[];
}

export const NAV_GROUPS: Array<{ key: NavGroupKey; labelKey: keyof Translations['nav'] }> = [
  { key: 'selling', labelKey: 'groupSelling' },
  { key: 'pricing', labelKey: 'groupPricing' },
  { key: 'menu', labelKey: 'groupMenu' },
  { key: 'stock', labelKey: 'groupStock' },
  { key: 'buying', labelKey: 'groupBuying' },
  { key: 'money', labelKey: 'groupMoney' },
  { key: 'admin', labelKey: 'groupAdmin' },
];

export const NAV_ITEMS: NavItem[] = [
  // --- selling -------------------------------------------------------------
  {
    path: '/dashboard',
    labelKey: 'dashboard',
    icon: 'LayoutDashboard',
    permission: 'dashboard.view',
    group: 'selling',
  },
  {
    path: '/pos',
    labelKey: 'pos',
    icon: 'ShoppingCart',
    permission: 'pos.access',
    group: 'selling',
    // Built in Phase 2. Taking payment and recording the sale is Phase 3, and
    // the cart panel says so on screen rather than offering a dead button.
    actions: [
      'sales.create',
      'sales.hold',
      'held_orders.create',
      'held_orders.restore',
      'orders.delivery_create',
      'sales.apply_discount',
      'sales.change_price',
      'sales.cancel_before_payment',
      'sales.reprint_receipt',
      'payments.cash',
      'payments.visa',
      'payments.whish',
      'payments.credit',
      'payments.mixed',
      'kitchen_tickets.print',
    ],
    implies: ['products.view'],
  },
  {
    // Between the till and the completed sales, because that is the order the
    // work happens in: a menu order arrives, it is confirmed into the till,
    // and it ends up in Orders once it is paid for.
    path: '/menu-orders',
    labelKey: 'menuOrders',
    icon: 'ClipboardList',
    // The permission that already governs reviewing menu orders. Confirming
    // and rejecting have their own keys and are checked on the buttons, so a
    // reviewer who may not decide can still see the queue.
    permission: 'menu.orders.view',
    group: 'selling',
    actions: ['menu.orders.confirm', 'menu.orders.reject'],
  },
  {
    path: '/orders',
    labelKey: 'orders',
    icon: 'ClipboardList',
    permission: 'sales.view_own',
    group: 'selling',
    // Built in Phase 3: completed sales, receipts and permitted reprints.
    // Held and preparing orders arrive with the kitchen workflow in Phase 7.
    actions: [
      'sales.view_all',
      // Seeing PAST days, which is a different question from whose sales you
      // may see. Listed here so the owner grants or removes it on this module
      // in the permission editor, beside the rest of what Orders can do.
      'sales.view_history',
      'sales.void_completed',
      'sales.refund',
      'sales.reprint_receipt',
      'kitchen_tickets.reprint',
    ],
  },
  {
    path: '/customers',
    labelKey: 'customers',
    icon: 'Users',
    permission: 'customers.view',
    group: 'selling',
    // PAUSED, not removed. A delivery no longer needs a customer record, so
    // the management screen waits for whatever the shop uses customers for
    // next. The data, the credit ledger and the till's picker all stay live.
    comingSoon: true,
    // Built in Phase 7. Credit figures inside are gated on customers.credit
    // and are filtered out of the response for anybody else.
    actions: [
      'customers.manage',
      'customers.credit',
    ],
  },
  {
    path: '/customer-credit',
    labelKey: 'customerCredit',
    icon: 'Wallet',
    permission: 'customers.credit',
    group: 'money',
    // Built in Phase 7: who owes what, and taking payments off account.
    actions: [
      'customers.credit',
    ],
  },

  // --- pricing and offers --------------------------------------------------
  //
  // Its own section, deliberately not under Stock: changing what the shop
  // charges is a selling decision, and the owner reaches for it from the
  // phone between customers. Both screens write through the same product
  // and offer services as the editors do - the keys are what differ.
  {
    path: '/pricing/quick',
    labelKey: 'quickPricing',
    icon: 'Zap',
    permission: 'pricing.quick_view',
    group: 'pricing',
    actions: ['pricing.quick_update'],
    implies: ['products.view'],
  },
  {
    path: '/pricing/offers',
    labelKey: 'offers',
    icon: 'Tag',
    permission: 'offers.view',
    group: 'pricing',
    actions: ['offers.manage', 'offers.poster'],
    implies: ['products.view'],
  },
  // ONE entry for the digital menu: the dashboard of boxes lives behind it.
  {
    path: '/menu',
    labelKey: 'menuManagement',
    icon: 'Globe',
    permission: 'menu.access',
    group: 'menu',
    actions: [
      'menu.products.manage',
      'menu.categories.manage',
      'menu.homepage.manage',
      'menu.settings.manage',
      'menu.orders.view',
      'menu.orders.confirm',
      'menu.orders.reject',
    ],
    implies: ['products.view', 'offers.view'],
  },

  // --- stock ---------------------------------------------------------------
  {
    path: '/products',
    labelKey: 'products',
    icon: 'Beef',
    // Guarded by products.update, NOT products.view.
    //
    // Every cashier holds products.view - the POS grid cannot draw a product
    // list without it. But this screen is where products are created, edited
    // and repriced, which a cashier must never reach. Using products.view here
    // would have put "Products" in the cashier's sidebar; a test caught it.
    permission: 'products.update',
    group: 'stock',
    // Built in Phase 2, including the Categories tab.
    actions: [
      'products.view',
      'products.create',
      'discounts.product_manage',
      'discounts.category_manage',
      'products.change_price',
      'products.view_cost',
      'category.print_routing_manage',
    ],
  },
  {
    path: '/inventory',
    labelKey: 'inventory',
    icon: 'Package',
    permission: 'inventory.view',
    group: 'stock',
    // «المخزون والجرد» - ONE screen: every product by category, its stock,
    // a box to correct it, and the five best sellers. Stock counts merged
    // in here; correcting is inventory.adjust and nothing more.
    // view_value is the dashboard's stock-value card - stock money, filed
    // with stock.
    actions: ['inventory.adjust', 'inventory.view_value'],
    implies: ['products.view'],
  },
  {
    path: '/recipes',
    labelKey: 'recipes',
    icon: 'ScrollText',
    // Its OWN key, not production.view. What a product is MADE OF and what was
    // MADE THIS MORNING are two different things to be trusted with; one key
    // opening both meant they could never be separated for anybody. Reading a
    // recipe is recipes.view, which a butcher holds - they need to know what
    // goes into the kafta. WRITING one is gated on products.update inside the
    // API, because it decides what a product costs.
    permission: 'recipes.view',
    group: 'stock',
    // Built in Phase 6.
    comingSoon: true,
  },
  {
    path: '/production',
    labelKey: 'production',
    icon: 'ChefHat',
    permission: 'production.view',
    group: 'stock',
    // Built in Phase 6: planning a run, finishing it, yield and cost.
    // Planning changes no stock; finishing deducts and adds in one step.
    comingSoon: true,
  },
  {
    path: '/waste',
    labelKey: 'waste',
    icon: 'Trash2',
    permission: 'waste.view',
    group: 'stock',
    // Built in Phase 6. Large losses need a manager's PIN.
    actions: [
      'waste.create',
      'waste.approve',
    ],
  },

  // --- buying --------------------------------------------------------------
  {
    path: '/purchases',
    labelKey: 'purchases',
    icon: 'Truck',
    permission: 'purchases.view',
    group: 'buying',
    // Built in Phase 5: orders, partial receiving, damaged and missing
    // quantities. Stock increases only when goods are actually received.
    actions: [
      'purchases.create',
      'purchases.receive',
    ],
  },
  {
    path: '/suppliers',
    labelKey: 'suppliers',
    icon: 'Factory',
    permission: 'suppliers.view',
    group: 'buying',
    // Built in Phase 5, with Invoices and Payments as tabs. Those two tabs
    // appear only for somebody allowed to see the money.
    actions: [
      'suppliers.manage',
    ],
  },

  // --- money ---------------------------------------------------------------
  {
    path: '/shifts',
    labelKey: 'myShift',
    icon: 'Clock',
    permission: 'shifts.view_own',
    group: 'money',
    // Built in Phase 3.
    actions: [
      'shifts.open',
      'shifts.close',
      'shifts.view_all',
      // Closing a cashier's forgotten drawer for them - owner only by default.
      'shifts.close_others',
    ],
  },
  {
    path: '/expenses',
    labelKey: 'expenses',
    icon: 'Receipt',
    permission: 'expenses.view',
    group: 'money',
    // Built in Phase 4. Attaching a photograph of the receipt comes later.
    actions: [
      'expenses.create',
      'expenses.approve',
    ],
  },
  {
    path: '/reports',
    labelKey: 'reports',
    icon: 'BarChart3',
    permission: 'reports.sales',
    group: 'money',
    // Phase 3 builds the daily sales report. The full suite - inventory,
    // purchases, cashier comparisons and exports - is Phase 8, and the page
    // itself lists what it does not yet cover.
    actions: [
      'reports.inventory',
      'reports.profit',
      'reports.cashiers',
    ],
  },

  // --- administration ------------------------------------------------------
  {
    path: '/employees',
    labelKey: 'employees',
    icon: 'UserCog',
    permission: 'employees.manage',
    group: 'admin',
    // Built in Phase 1, read-only. The screen itself says so plainly.
    actions: [
      'employees.manage',
    ],
  },
  {
    path: '/audit-log',
    labelKey: 'auditLog',
    icon: 'ScrollText',
    permission: 'audit.view',
    group: 'admin',
    // Built in Phase 1 and already recording real sign-ins.
  },
  // --- Phase 9 -------------------------------------------------------------
  {
    path: '/offline-queue',
    labelKey: 'offlineQueue',
    icon: 'CloudOff',
    // Anybody who may sell may see what this till has not sent yet. It is
    // their own work sitting in that queue.
    permission: 'sales.create',
    group: 'selling',
    actions: [
      'sales.create',
    ],
  },
  {
    path: '/labels',
    labelKey: 'labels',
    icon: 'Tags',
    // `inventory.view`, not `products.view`. Labelling a packed tray is a
    // stock job done by whoever packed it - a butcher or the inventory
    // employee - and every cashier holds `products.view` because the POS grid
    // needs it. Gating on that would have put label printing, and the price it
    // states, in front of the till.
    permission: 'inventory.view',
    group: 'stock',
    comingSoon: true,
  },

  // --- Phase 8 -------------------------------------------------------------
  {
    path: '/import',
    labelKey: 'importData',
    icon: 'Upload',
    // The outer door. Each kind of file needs its own permission on top,
    // checked by the server - a manager may import products without that
    // implying they may import anything.
    permission: 'data.import',
    group: 'admin',
  },
  {
    path: '/backups',
    labelKey: 'backups',
    icon: 'DatabaseBackup',
    // Owner only. Backups are the last line between the shop and losing
    // everything, so they are not a manager's responsibility to get wrong.
    permission: 'backups.manage',
    group: 'admin',
  },
  {
    path: '/settings',
    labelKey: 'settings',
    icon: 'Settings',
    permission: 'settings.manage',
    group: 'admin',
    actions: [
      'settings.manage',
      'exchange_rate.update',
    ],
  },
];

// Embedded business surfaces retain assignable permissions without a sidebar entry.
export const PERMISSION_MODULES: NavItem[] = [
  ...NAV_ITEMS,
  {
    path: '/pos?view=accounts', labelKey: 'customerOrders', icon: 'Truck',
    permission: 'orders.delivery_view', group: 'selling',
    // collect_others: receiving another cashier's delivery, into THEIR drawer.
    actions: ['orders.delivery_create', 'orders.delivery_manage', 'orders.collect_others'],
    implies: ['customers.lookup'],
  },
];

/// Only the items this employee may actually open.
export function visibleNavItems(permissions: Set<string>): NavItem[] {
  return NAV_ITEMS.filter((item) => permissions.has(item.permission));
}

/// Where to send someone after they sign in.
///
/// A cashier has no dashboard, so sending everyone to /dashboard would greet
/// half the staff with a permission refusal. Instead each person lands on the
/// first screen they are actually allowed to use, in a sensible order.
export function landingPathFor(permissions: Set<string>): string {
  const preference = ['/dashboard', '/pos', '/orders', '/products', '/inventory', '/employees', '/audit-log'];

  for (const path of preference) {
    const item = NAV_ITEMS.find((navItem) => navItem.path === path);
    if (item && permissions.has(item.permission)) return path;
  }

  const first = visibleNavItems(permissions)[0];
  return first ? first.path : '/unauthorized';
}
